import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
    LayoutDashboard, Package, PlusCircle, Video, ShoppingBag, 
    Store, Bell, Settings, ArrowLeft, ArrowRight, Menu, X, 
    Search, CheckCircle, AlertTriangle, ShieldCheck, Globe,
    BarChart2, MessageSquare, Share2, Radio, Plus, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getVendorApplicationStatus, getSellerChatThreads, getUserId } from '../api/client';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';
import VendorApplicationPage from '../pages/VendorApplicationPage';
import './seller.css';

export default function SellerLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const rtl = isRTL();

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [vendorStatus, setVendorStatus] = useState(null);
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 900 : false);
    const [realUnreadCount, setRealUnreadCount] = useState(0);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 900);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isWizardPage = location.pathname.includes('/products/new');

    useEffect(() => {
        if (!authLoading) {
            if (!isAuthenticated) {
                navigate('/login?redirect=' + encodeURIComponent(location.pathname));
                return;
            }

            // Check vendor application status
            getVendorApplicationStatus()
                .then(res => {
                    const data = res?.data && res.data !== res ? res.data : (res?.status ? res : (res?.data || null));
                    if (data && (data.status || data.id)) {
                        setVendorStatus(data);
                    }
                })
                .catch(() => {
                    setVendorStatus(null);
                })
                .finally(() => {
                    setCheckingStatus(false);
                });
        }
    }, [isAuthenticated, authLoading, navigate, location.pathname]);

    // Live Unread Count from Real Backend (Zero Fake Data)
    useEffect(() => {
        const fetchUnread = async () => {
            try {
                const uid = user?.id || getUserId() || localStorage.getItem('userId');
                if (uid) {
                    const res = await getSellerChatThreads(uid);
                    const threads = res?.data || (Array.isArray(res) ? res : []);
                    const unreadSum = threads.reduce((acc, t) => acc + (t.unreadCount || (t.unread ? 1 : 0)), 0);
                    setRealUnreadCount(unreadSum);
                }
            } catch (_) {}
        };
        fetchUnread();
        const interval = setInterval(fetchUnread, 15000);
        return () => clearInterval(interval);
    }, [user]);

    // Guarantee zero top-padding and clean luxury background on mobile & desktop
    useEffect(() => {
        const mainEl = document.querySelector('.main-content');
        const rootEl = document.querySelector('.luxvono-root');
        const originalMainPadding = mainEl ? mainEl.style.paddingTop : '';
        const originalRootBg = rootEl ? rootEl.style.backgroundColor : '';
        const originalBodyBg = document.body.style.backgroundColor;

        if (mainEl) {
            mainEl.style.paddingTop = '0px';
        }

        return () => {
            if (mainEl) {
                mainEl.style.paddingTop = originalMainPadding;
                mainEl.style.backgroundColor = '';
            }
            if (rootEl) rootEl.style.backgroundColor = '';
            document.body.style.backgroundColor = '';
        };
    }, []);

    // Close mobile drawer on route change
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    if (authLoading || checkingStatus) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
                <Spinner />
            </div>
        );
    }

    const userRoleStr = (user?.role || localStorage.getItem('userRole') || '').toUpperCase();
    const allRoles = (user?.roles || []).map(r => String(r).toUpperCase());
    const isAdmin = userRoleStr.includes('ADMIN') || allRoles.some(r => r.includes('ADMIN'));
    const isVendorRole = userRoleStr.includes('VENDOR') || allRoles.some(r => r.includes('VENDOR'));
    const isStatusApproved = vendorStatus?.status === 'APPROVED';

    // Strict security check: Must be admin, have VENDOR role, or application explicitly APPROVED, and NOT BANNED
    const isBanned = vendorStatus?.status === 'BANNED' || vendorStatus?.status === 'SUSPENDED';
    const isApprovedVendor = !isBanned && (isAdmin || isStatusApproved || isVendorRole);

    // Strict Gating: Unapproved or banned users directly see the official application / ban flow
    if (!isApprovedVendor) {
        return <VendorApplicationPage />;
    }

    const storeInitial = (vendorStatus?.businessName || user?.fullName || 'L').charAt(0).toUpperCase();

    return (
        <div className={`seller-container ${rtl ? 'rtl' : ''}`}>
            {/* Sidebar (Desktop & Mobile Drawer) */}
            <aside className={`seller-sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="seller-sidebar-header">
                    <Link to="/seller/dashboard" className="seller-brand">
                        <div className="seller-brand-logo">L</div>
                        <div className="seller-brand-text">
                            <h2>LUXVONO</h2>
                            <span>SELLER HUB</span>
                        </div>
                    </Link>
                    <button 
                        onClick={() => setSidebarOpen(false)}
                        style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                        className="mobile-close-btn"
                        aria-label="إغلاق"
                    >
                        <X size={20} />
                    </button>
                </div>

                <nav className="seller-nav">
                    <NavLink to="/seller/dashboard" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <LayoutDashboard size={18} />
                        <span>{rtl ? 'لوحة التحكم' : 'Dashboard'}</span>
                    </NavLink>

                    <NavLink to="/seller/products" end className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <Package size={18} />
                        <span>{rtl ? 'منتجاتي ومخزوني' : 'My Products'}</span>
                    </NavLink>

                    <NavLink to="/seller/products/new" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <PlusCircle size={18} color="#FE2C55" />
                        <span style={{ color: '#FE2C55', fontWeight: 700 }}>{rtl ? 'إضافة منتج فاخر' : 'Add Product'}</span>
                    </NavLink>

                    <NavLink to="/seller/orders" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <ShoppingBag size={18} />
                        <span>{rtl ? 'الطلبات والمبيعات' : 'Orders'}</span>
                    </NavLink>

                    <NavLink to="/seller/messages" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <MessageSquare size={18} />
                        <span>{rtl ? 'رسائل العملاء' : 'Messages'}</span>
                    </NavLink>

                    <NavLink to="/seller/posts" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <Share2 size={18} />
                        <span>{rtl ? 'منشورات الترويج' : 'Social Posts'}</span>
                    </NavLink>

                    <NavLink to="/seller/live" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <Radio size={18} color="#FE2C55" />
                        <span style={{ color: '#FE2C55', fontWeight: 600 }}>{rtl ? 'البث المباشر (LIVE)' : 'Live Commerce'}</span>
                    </NavLink>

                    <NavLink to="/seller/videos" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <Video size={18} />
                        <span>{rtl ? 'مقاطع الفيديو (Reels)' : 'Product Videos'}</span>
                    </NavLink>

                    <NavLink to="/seller/analytics" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <BarChart2 size={18} />
                        <span>{rtl ? 'التحليلات والمؤشرات' : 'Analytics & Stats'}</span>
                    </NavLink>

                    <NavLink to="/seller/store" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <Store size={18} />
                        <span>{rtl ? 'متجري الفاخر' : 'My Store'}</span>
                    </NavLink>

                    <NavLink to="/seller/notifications" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <Bell size={18} />
                        <span>{rtl ? 'إشعارات المراجعة' : 'Notifications'}</span>
                    </NavLink>

                    <NavLink to="/seller/settings" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`}>
                        <Settings size={18} />
                        <span>{rtl ? 'إعدادات الحساب' : 'Settings'}</span>
                    </NavLink>

                    <NavLink to="/seller/admin-control" className={({ isActive }) => `seller-nav-item ${isActive ? 'active' : ''}`} style={{ marginTop: 8, borderTop: '1px solid rgba(16,185,129,0.2)', paddingTop: 12 }}>
                        <ShieldCheck size={18} color="#10B981" />
                        <span style={{ color: '#10B981', fontWeight: 700 }}>{rtl ? 'رقابة الإدارة (Admin)' : 'Admin Control'}</span>
                    </NavLink>
                </nav>

                <div className="seller-sidebar-footer">
                    <Link to="/" className="back-to-storefront-btn">
                        {rtl ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
                        <span>{rtl ? 'العودة للمتجر الرئيسي' : 'Back to Storefront'}</span>
                    </Link>
                </div>
            </aside>

            {/* Main Area */}
            <div className="seller-main">
                {/* 1. Mobile Native App Header (Sleek, zero-clutter) */}
                {!isWizardPage && (
                    <header className="seller-mobile-appbar">
                        <div className="seller-mobile-appbar-start">
                            <button 
                                onClick={() => setSidebarOpen(true)}
                                className="seller-mobile-icon-btn"
                                aria-label="القائمة الجانبية"
                            >
                                <Menu size={20} />
                            </button>

                            <Link to="/seller/store" className="seller-mobile-avatar" style={{ textDecoration: 'none' }}>
                                {vendorStatus?.logoUrl ? (
                                    <img src={vendorStatus.logoUrl} alt="Store" />
                                ) : (
                                    <span>{storeInitial}</span>
                                )}
                            </Link>

                            <Link to="/seller/store" className="seller-mobile-store-info" style={{ textDecoration: 'none' }}>
                                <div className="seller-mobile-store-title-row">
                                    <span className="seller-mobile-store-name">
                                        {vendorStatus?.businessName || user?.fullName || (rtl ? 'متجر التاجر' : 'Seller Store')}
                                    </span>
                                    <CheckCircle size={14} className="seller-verified-icon" />
                                </div>
                                <span className="seller-mobile-store-sub">
                                    {rtl ? 'تاجر معتمد • ⭐ 5.0' : 'Verified • ⭐ 5.0'}
                                </span>
                            </Link>
                        </div>

                        <div className="seller-mobile-appbar-end">
                            <Link to="/" className="seller-mobile-icon-btn" title={rtl ? 'تطبيق العميل' : 'Store'}>
                                <Store size={18} />
                            </Link>
                        </div>
                    </header>
                )}

                {/* 2. Desktop Topbar */}
                {(!isMobile || !isWizardPage) && (
                    <header className="seller-topbar">
                        <div className="seller-topbar-left">
                            <button 
                                onClick={() => setSidebarOpen(true)}
                                style={{ background: 'none', border: 'none', color: '#111827', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            >
                                <Menu size={22} />
                            </button>
                            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                                {vendorStatus?.businessName ? (
                                    <strong style={{ color: '#111827' }}>{vendorStatus.businessName}</strong>
                                ) : (
                                    rtl ? 'بوابة المورد المعتمد' : 'Verified Vendor Portal'
                                )}
                            </span>
                        </div>

                        <div className="seller-topbar-right">
                            {isApprovedVendor ? (
                                <span className="status-badge approved">
                                    <CheckCircle size={14} />
                                    {rtl ? 'تاجر معتمد' : 'Verified Merchant'}
                                </span>
                            ) : (
                                <Link to="/vendor/apply" className="status-badge pending" style={{ textDecoration: 'none' }}>
                                    <AlertTriangle size={14} />
                                    {rtl ? 'توثيق الحساب التجاري' : 'Complete Verification'}
                                </Link>
                            )}

                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Link 
                                    to="/" 
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '7px 16px',
                                        borderRadius: 20,
                                        background: '#f3f4f6',
                                        border: '1px solid #e5e7eb',
                                        color: '#111827',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        textDecoration: 'none',
                                        transition: 'all 0.2s ease'
                                    }}
                                    title={rtl ? 'خروج لتطبيق العملاء' : 'Exit to Customer Store'}
                                >
                                    <Store size={15} />
                                    <span>{rtl ? 'خروج لتطبيق العملاء' : 'Exit to Customer'}</span>
                                </Link>
                                <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, color: '#ffffff' }}>
                                    {storeInitial}
                                </div>
                            </div>
                        </div>
                    </header>
                )}

                {/* 3. Workspace Content */}
                <main className={`seller-workspace ${isWizardPage ? 'wizard-mode' : ''}`}>
                    <Outlet context={{ isApprovedVendor, vendorStatus }} />
                </main>

                {/* 4. Native Mobile Bottom Navigation Bar */}
                {!isWizardPage && (
                    <nav className="seller-mobile-bottom-nav">
                        <NavLink to="/seller/dashboard" className={({ isActive }) => `seller-bottom-nav-item ${isActive ? 'active' : ''}`}>
                            <LayoutDashboard size={20} />
                            <span>{rtl ? 'الرئيسية' : 'Home'}</span>
                        </NavLink>

                        <NavLink to="/seller/products" className={({ isActive }) => `seller-bottom-nav-item ${isActive ? 'active' : ''}`}>
                            <Package size={20} />
                            <span>{rtl ? 'المنتجات' : 'Products'}</span>
                        </NavLink>

                        <Link to="/seller/products/new" className="seller-bottom-nav-add-btn" title={rtl ? 'إضافة منتج جديد' : 'Add Product'}>
                            <Plus size={24} />
                        </Link>

                        <NavLink to="/seller/orders" className={({ isActive }) => `seller-bottom-nav-item ${isActive ? 'active' : ''}`}>
                            <ShoppingBag size={20} />
                            <span>{rtl ? 'الطلبات' : 'Orders'}</span>
                        </NavLink>

                        <NavLink to="/seller/messages" className={({ isActive }) => `seller-bottom-nav-item ${isActive ? 'active' : ''}`}>
                            <div style={{ position: 'relative', display: 'inline-flex' }}>
                                <MessageSquare size={20} />
                                {realUnreadCount > 0 && (
                                    <span className="seller-bottom-badge">
                                        {realUnreadCount > 99 ? '99+' : realUnreadCount}
                                    </span>
                                )}
                            </div>
                            <span>{rtl ? 'الرسائل' : 'Chat'}</span>
                        </NavLink>
                    </nav>
                )}
            </div>
        </div>
    );
}
